import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from "../../components/AuthContext/AuthContext";
import { URL_TO_TEST_TYPE, TEST_TYPES } from "../../utils";
import DynamicForm from '../../stories/Forms/DynamicForm'
import { InfoCircle, XSquare } from 'react-bootstrap-icons'
import AccountCard from '../../stories/cards/AccountCard'
import useApis from "../../services/useAPI";
import * as yup from "yup";
import { useMediaQuery } from "react-responsive";

const PhlebotomyLogin =  () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { callApi, loading, serverError} = useApis();
  const params = new URLSearchParams(location.search)
  const next_url = params.get('next')
  const [formData, setFormData] = useState({})
  const [accountExists, setAccountExists] = useState(null)
  const [isCheckingEmail, setIsCheckingEmail] = useState(false)
  const isMobile = useMediaQuery({ maxWidth: 767 });

  const pathSegments = location.search ? location.search.split('%2F') : []

  let matchedTestType = null

  pathSegments.forEach(segment => {
    if (URL_TO_TEST_TYPE[segment]) {
      matchedTestType = URL_TO_TEST_TYPE[segment]
    }
    if (TEST_TYPES[segment]) {
      matchedTestType = TEST_TYPES[segment]
    }
  })

  if (matchedTestType) {
    localStorage.setItem('test_type', matchedTestType)
  }

  const extractBidFromUrl = () => { 
    if (location.search) {
      let lastSegment;
      const searchSegments = location.search.split('%2F').filter(segment => segment);
      if(searchSegments.includes("assessment"))
      {
        lastSegment = searchSegments[searchSegments.length - 2];
      }
      else{
        lastSegment = searchSegments[searchSegments.length - 1];
      }
      if (lastSegment && !isNaN(lastSegment)) {
        return lastSegment;
      }
    }
    
    return null;
  };

  const bid = extractBidFromUrl();

  // Show error toast if server error occurs
  useEffect(() => {
    localStorage.setItem("is_phlebotomist",true)
  }, [serverError, auth]);

  useEffect(() => {
    if (serverError) {
      auth?.showToast("Error", serverError, "../assets/mhc.png", <XSquare />);
    }
  }, [serverError])

  // Check if email exists using the API endpoint   
  const checkEmailExists = async (email) => {
    setIsCheckingEmail(true);
    try {
      const requestPayload = { email };
      if (bid) {
        requestPayload.bid = bid;
      }

      const response = await callApi(requestPayload, '/api/dashboard/check-email', 'POST');
      
      if (response) {
        const userExists = response.present === true;
        
        if (userExists) {
          
          auth?.showToast(
            'Account already exist',
            'Please enter your password to register your appointment',
            '../assets/mhc.png',
            <InfoCircle />
          );
          
          // Preserve the next_url parameter or use the current one
          const redirectUrl = next_url || '/bloods/boots/blood-energy';
          const redirectPath = `/login?next=${encodeURIComponent(redirectUrl)}&email=${encodeURIComponent(email)}`;
          
          // Use navigate without setTimeout for immediate redirect
          navigate(redirectPath, { replace: true });
          
          return true;
        } else {
          // Account doesn't exist - redirect to create account
          setAccountExists(false);
          auth?.showToast(
            'Account Not Found',
            `Registration is required before attending your Boots appointment`,
            '../assets/mhc.png',
            <InfoCircle />
          );
          
          // Redirect to create account page with email pre-filled
          setTimeout(() => {
            navigate(`/create-account/${location.search}&email=${encodeURIComponent(email)}`, { 
              state: { email: email, preVerified: false } 
            });
          }, 700);
        }
        
        return userExists;
      }
      return false;
    } catch (error) {
      auth?.showToast(
        'Error',
        'Unable to verify email. Please try again.',
        '../assets/mhc.png',
        <XSquare />
      );
      return false;
    } finally {
      setIsCheckingEmail(false);
    }
  }

  const execute = async (data) => {
    // Check if email exists (first submission)
    if (accountExists === null && data.email) {
      await checkEmailExists(data.email);
      setFormData({ email: data.email });
    }
  }

  useEffect(() => {
    if (auth?.user) {
      navigate(next_url || '/', { replace: true });
    }
  }, [auth?.user, navigate, next_url]);

  // Build fields - only email field needed
  let fields = []

  // Email field - always show
  fields.push({
    controlId: "email",
    label: "",
    type: "email",
    name: "email",
    required: true,
    max_length: 127,
    autoComplete: "username",
    placeholder: "Enter your email"
  })

  // Add submit button
  const submitButton = {
    controlId: "submit",
    label: "Confirm & Continue",
    type: "submit",
    size: "large",
    variant: "primary",
    className: "m-0 fs-18 btn-green ageo-medium",
    isLoading: loading || isCheckingEmail
  }
  
  fields.push(submitButton)

  // Create validation schema
  const getValidationSchema = () => {
    let schemaShape = {
      email: yup.string().required("Email is required").email("Invalid email format")
    }
    
    return yup.object().shape(schemaShape)
  }

  // Helper text
  const getHelperText = () => {
    return (
      <>
        <p className='phlebo-desc'>
          You’re registering for your test as part of the Boots In-Store
          Wellness Testing service, powered by MyHealthChecked. Before your
          appointment, you’ll need to provide a few details and complete a short
          health assessment.
        </p>
        <h3 className='text-teal'>Enter your email address to begin.</h3>
        <p className='fs-16'>If you’re new, we’ll help you set up an account as part of the
        registration process.</p>
      </>
    );
  };

  return (
    <AccountCard isphlebo={true} title="Let’s Get You Started!" className="initial-form-wrapper">
      <div className={`${isMobile ? "mb-3 pb-1" : "mb-4"}`}>
        <p className="text-muted small mb-0">
          {getHelperText()}
        </p>
      </div>
      
      <DynamicForm
        fields={fields}
        validationSchema={getValidationSchema()}
        initialValues={formData}
        onSubmit={execute}
        onChange={setFormData}
        isLoading={loading || isCheckingEmail}
        error={serverError}
      />
    </AccountCard>
  )
}

export default PhlebotomyLogin;